import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../elements/Header";
import Footer from "../../elements/Footer";
import { getAuth } from "firebase/auth";
import { useFirestore } from "../../firebase/FirestoreContext";
import { collectionPath } from "../../context/EnvContext";
import addToCart from "../../functions/addToCart";
import createAnonymousUser from "../../functions/createAnonymousUser";
import { CircularProgress, Box, Typography } from "@mui/material";

const CartLoader = () => {
  const { base64Data } = useParams();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const auth = getAuth();
  const user = auth.currentUser;

  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    if (progress.total > 0) return;

    const processCartData = async () => {
      try {
        // Decode base64 data
        const decodedData = atob(base64Data);
        const cartItems = JSON.parse(decodedData);

        if (!Array.isArray(cartItems)) {
          throw new Error("Invalid cart data format");
        }

        setProgress({ current: 0, total: cartItems.length });

        // Ensure user exists before adding items to prevent race conditions
        let currentUser = user;
        if (!currentUser) {
          currentUser = await createAnonymousUser();
        }

        // Process each item sequentially to avoid race conditions
        for (let index = 0; index < cartItems.length; index++) {
          const item = cartItems[index];

          if (!item.product_id || !item.quantity) {
            console.warn("Skipping invalid item:", item);
            setProgress({ current: index + 1, total: cartItems.length });
            continue;
          }

          try {
            await addToCart(
              item.product_id,
              currentUser,
              firestore,
              item.quantity,
              collectionPath,
              { overrideQuantity: true },
            );
          } catch (itemError) {
            console.error(`Error adding item ${item.product_id}:`, itemError);
          }

          setProgress({ current: index + 1, total: cartItems.length });
        }

        // Navigate to checkout page after processing
        navigate("/checkout");

      } catch (err) {
        console.error("Error processing cart data:", err);
        setError("Failed to process cart data. Please check the URL and try again.");
      }
    };

    if (base64Data) {
      processCartData();
    } else {
      setError("No cart data provided");
    }
  }, [base64Data, firestore, user, collectionPath, navigate, progress]);

  if (error) {
    return (
      <div>
        <Header search={false} />
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="50vh"
          padding={3}
        >
          <Typography variant="h5" color="error" gutterBottom>
            Error Loading Cart
          </Typography>
          <Typography variant="body1" color="textSecondary" textAlign="center">
            {error}
          </Typography>
        </Box>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <Header search={false} />
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="50vh"
        padding={3}
      >
        <CircularProgress size={60} style={{ marginBottom: 20 }} />
        <Typography variant="h5" gutterBottom>
          Loading Cart...
        </Typography>
        <Typography variant="body1" color="textSecondary" textAlign="center">
          Adding items to your cart
        </Typography>
        {progress.total > 0 && (
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
            {progress.current} of {progress.total} items processed
          </Typography>
        )}
      </Box>
      <Footer />
    </div>
  );
};

export default CartLoader;
